import { supabase } from "@/integrations/supabase/client";
import { ACTIVE_TEST_ID, getVariant } from "@/lib/abTest";

/**
 * First-party website tracking for the Super Admin dashboard.
 * Records real sessions, page views, engagement and click events.
 *
 * Privacy rules:
 *  - No fingerprinting, no cross-site tracking, no exact addresses.
 *  - IP addresses are never stored client-side or server-side; they are used
 *    in-memory by the geo-enrich function only for approximate city/state.
 *  - The visitor ID is a random first-party value stored in this browser only.
 */

const SESSION_KEY_STORAGE = "jttg_session_key";
const ATTRIBUTION_STORAGE = "jttg_attribution";
const VISITOR_STORAGE = "jttg_visitor";
const SESSION_TTL_MS = 30 * 60 * 1000;

export interface VisitorRecord {
  visitorKey: string;
  firstSeenAt: number;
  lastSeenAt: number;
  visits: number;
  firstTouchSource?: string;
  firstTouchPage?: string;
}

export interface Attribution {
  sessionKey: string;
  visitorKey: string;
  visitNumber: number;
  isReturning: boolean;
  firstLandingPage: string;
  referrer: string;
  referrerDomain: string;
  trafficSource: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  fbclid?: string;
  deviceType: string;
  browser: string;
  startedAt: number;
  lastSeenAt: number;
  lastPage: string;
  engagementSeconds: number;
  maxScrollDepth: number;
  intentScore: number;
  intentFlags: string[];
  viewedPaths: string[];
  primaryInterest?: string;
}

const isBrowser = () => typeof window !== "undefined";

const uid = (prefix: string) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

const getDeviceType = (): string => {
  if (!isBrowser()) return "unknown";
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
};

const getBrowser = (): string => {
  if (!isBrowser()) return "unknown";
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Other";
};

const domainOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/** Classify a visit into one of the dashboard traffic-source buckets. */
export const classifySource = (
  referrer: string,
  params: URLSearchParams,
): string => {
  const utmSource = (params.get("utm_source") || "").toLowerCase();
  const utmMedium = (params.get("utm_medium") || "").toLowerCase();

  if (params.get("gclid") || (utmSource === "google" && /cpc|ppc|paid/.test(utmMedium)))
    return "Google Ads";
  if (utmMedium === "qr" || utmSource === "qr" || params.get("qr")) return "QR Codes";
  if (utmMedium === "email" || /newsletter|klaviyo|mailchimp/.test(utmSource))
    return "Email Campaigns";
  if (/gbp|google-business|gmb/.test(utmSource)) return "Google Business Profile";
  if (params.get("fbclid") || /facebook|fb/.test(utmSource)) return "Facebook";
  if (/instagram|ig/.test(utmSource)) return "Instagram";
  if (/tiktok/.test(utmSource)) return "TikTok";
  if (/linkedin/.test(utmSource)) return "LinkedIn";

  const host = domainOf(referrer);
  if (!referrer || !host) return "Direct Traffic";
  if (/google\./.test(host)) return "Google Organic Search";
  if (/bing\.|duckduckgo\.|yahoo\./.test(host)) return "Other Search";
  if (/facebook\.|fb\.com/.test(host)) return "Facebook";
  if (/instagram\./.test(host)) return "Instagram";
  if (/tiktok\./.test(host)) return "TikTok";
  if (/linkedin\./.test(host)) return "LinkedIn";
  if (host === window.location.hostname.replace(/^www\./, "")) return "Direct Traffic";
  return "Referral Websites";
};

/* ---------------- Trailer interest ---------------- */

const INTEREST_MAP: { label: string; test: RegExp }[] = [
  { label: "Clearance Inventory", test: /clearance/ },
  { label: "Financing", test: /financing|rent-to-own|lease-to-own/ },
  { label: "Dump Trailers", test: /dump/ },
  { label: "Flatbed Trailers", test: /flatbed/ },
  { label: "Equipment Trailers", test: /equipment/ },
  { label: "Lowboy Trailers", test: /lowboy/ },
  { label: "Gravity Tilt Trailers", test: /gravity-tilt/ },
  { label: "Power Tilt Trailers", test: /power-tilt/ },
  { label: "Tilt Trailers", test: /tilt/ },
  { label: "Car Haulers", test: /car-hauler/ },
  { label: "Roll-Off Trailers", test: /roll-off/ },
  { label: "Dumpsters", test: /dumpster/ },
  { label: "Non-CDL Hotshot", test: /hotshot|non-cdl/ },
  { label: "Gooseneck Trailers", test: /gooseneck/ },
  { label: "Trailer Parts", test: /parts|tire|axle|mrovi/ },
];

export const interestForPath = (path: string): string | null => {
  const p = path.toLowerCase();
  for (const entry of INTEREST_MAP) if (entry.test.test(p)) return entry.label;
  return null;
};

/* ---------------- Buyer intent scoring ---------------- */

export const INTENT_RULES: Record<string, number> = {
  trailer_page: 10,
  trailer_page_repeat: 15,
  inventory_page: 10,
  financing_page: 20,
  phone_click: 30,
  email_click: 20,
  quote_page: 25,
  quote_started: 40,
  quote_submitted: 100,
  multi_visit: 15,
  returned_within_7_days: 15,
};

export type IntentLevel = "LOW" | "INTERESTED" | "HIGH" | "VERY HIGH" | "LEAD";

export const intentLevel = (score: number): IntentLevel => {
  if (score >= 100) return "LEAD";
  if (score >= 80) return "VERY HIGH";
  if (score >= 50) return "HIGH";
  if (score >= 21) return "INTERESTED";
  return "LOW";
};

/* ---------------- Storage ---------------- */

const readStored = (): Attribution | null => {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
};

const persist = (a: Attribution) => {
  try {
    localStorage.setItem(ATTRIBUTION_STORAGE, JSON.stringify(a));
    localStorage.setItem(SESSION_KEY_STORAGE, a.sessionKey);
  } catch {
    /* storage unavailable — tracking degrades silently */
  }
};

const readVisitor = (): VisitorRecord | null => {
  try {
    const raw = localStorage.getItem(VISITOR_STORAGE);
    return raw ? (JSON.parse(raw) as VisitorRecord) : null;
  } catch {
    return null;
  }
};

const saveVisitor = (v: VisitorRecord) => {
  try {
    localStorage.setItem(VISITOR_STORAGE, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};

/** Returns (and creates when needed) the persistent anonymous visitor record. */
const touchVisitor = (): { record: VisitorRecord; returnedWithin7Days: boolean } => {
  const now = Date.now();
  const existing = readVisitor();
  if (!existing) {
    const record: VisitorRecord = {
      visitorKey: uid("v"),
      firstSeenAt: now,
      lastSeenAt: now,
      visits: 1,
    };
    saveVisitor(record);
    return { record, returnedWithin7Days: false };
  }
  const gap = now - existing.lastSeenAt;
  const record: VisitorRecord = {
    ...existing,
    visits: existing.visits + 1,
    lastSeenAt: now,
  };
  saveVisitor(record);
  return { record, returnedWithin7Days: gap <= 7 * 24 * 60 * 60 * 1000 };
};

/* ---------------- Session lifecycle ---------------- */

/** Returns the current attribution record, creating a session when needed. */
export const getAttribution = (): Attribution | null => {
  if (!isBrowser()) return null;
  const existing = readStored();
  const now = Date.now();

  if (existing && now - existing.lastSeenAt < SESSION_TTL_MS) {
    // Older stored records may predate newer fields — normalise defensively.
    existing.viewedPaths = Array.isArray(existing.viewedPaths) ? existing.viewedPaths : [];
    existing.intentFlags = Array.isArray(existing.intentFlags) ? existing.intentFlags : [];
    existing.intentScore = Number.isFinite(existing.intentScore) ? existing.intentScore : 0;
    existing.engagementSeconds = Number.isFinite(existing.engagementSeconds)
      ? existing.engagementSeconds
      : 0;
    existing.maxScrollDepth = Number.isFinite(existing.maxScrollDepth)
      ? existing.maxScrollDepth
      : 0;
    existing.lastSeenAt = now;
    persist(existing);
    return existing;
  }


  const { record, returnedWithin7Days } = touchVisitor();
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || "";
  const intentFlags: string[] = [];
  let intentScore = 0;
  if (record.visits > 1) {
    intentFlags.push("multi_visit");
    intentScore += INTENT_RULES.multi_visit;
  }
  if (returnedWithin7Days) {
    intentFlags.push("returned_within_7_days");
    intentScore += INTENT_RULES.returned_within_7_days;
  }

  const fresh: Attribution = {
    sessionKey: uid("s"),
    visitorKey: record.visitorKey,
    visitNumber: record.visits,
    isReturning: record.visits > 1,
    firstLandingPage: window.location.pathname,
    referrer,
    referrerDomain: domainOf(referrer),
    trafficSource: classifySource(referrer, params),
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || params.get("qr") || undefined,
    utmContent: params.get("utm_content") || undefined,
    utmTerm: params.get("utm_term") || undefined,
    gclid: params.get("gclid") || undefined,
    fbclid: params.get("fbclid") || undefined,
    deviceType: getDeviceType(),
    browser: getBrowser(),
    startedAt: now,
    lastSeenAt: now,
    lastPage: window.location.pathname,
    engagementSeconds: 0,
    maxScrollDepth: 0,
    intentScore,
    intentFlags,
    viewedPaths: [],
    primaryInterest: interestForPath(window.location.pathname) ?? undefined,
  };
  if (!record.firstTouchSource) {
    saveVisitor({
      ...record,
      firstTouchSource: fresh.trafficSource,
      firstTouchPage: fresh.firstLandingPage,
    });
  }

  persist(fresh);
  sessionReady = createSession(fresh);
  return fresh;
};

/** Resolves once the current session row exists, so later updates never race. */
let sessionReady: Promise<void> = Promise.resolve();

const createSession = async (a: Attribution) => {
  try {
    await supabase.from("site_sessions").insert({
      session_key: a.sessionKey,
      visitor_key: a.visitorKey,
      visit_number: a.visitNumber,
      is_returning: a.isReturning,
      first_landing_page: a.firstLandingPage,
      last_page: a.lastPage,
      exit_page: a.lastPage,
      referrer: a.referrer || null,
      referrer_domain: a.referrerDomain || null,
      traffic_source: a.trafficSource,
      utm_source: a.utmSource ?? null,
      utm_medium: a.utmMedium ?? null,
      utm_campaign: a.utmCampaign ?? null,
      utm_content: a.utmContent ?? null,
      utm_term: a.utmTerm ?? null,
      gclid: a.gclid ?? null,
      fbclid: a.fbclid ?? null,
      device_type: a.deviceType,
      browser: a.browser,
      page_view_count: 0,
      intent_score: a.intentScore,
      primary_interest: a.primaryInterest ?? null,
    });
    // Approximate city/state enrichment — IP never leaves the server.
    void supabase.functions.invoke("geo-enrich", {
      body: { session_key: a.sessionKey },
    });
  } catch {
    /* never let tracking break the page */
  }
};

/**
 * Adds intent points once per rule (local mirror only).
 * The authoritative score is recomputed server-side from the event stream.
 */
const addIntent = async (a: Attribution, rule: keyof typeof INTENT_RULES, repeatable = false) => {
  if (!repeatable && a.intentFlags.includes(rule)) return;
  a.intentFlags.push(rule);
  a.intentScore += INTENT_RULES[rule];
  persist(a);
};

const dominantInterest = (paths: string[]): string | undefined => {
  const counts = new Map<string, number>();
  paths.forEach((p) => {
    const label = interestForPath(p);
    if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  let best: string | undefined;
  let bestCount = 0;
  counts.forEach((count, label) => {
    if (count > bestCount) {
      best = label;
      bestCount = count;
    }
  });
  return best;
};

/** Records a page view and keeps the session "live" for the Live Traffic tab. */
export const trackPageView = async (path: string): Promise<void> => {
  const a = getAttribution();
  if (!a) return;
  const repeatView = a.viewedPaths.includes(path);
  a.lastPage = path;
  a.lastSeenAt = Date.now();
  a.viewedPaths = [...a.viewedPaths, path].slice(-60);
  a.primaryInterest = dominantInterest(a.viewedPaths) ?? a.primaryInterest;
  persist(a);

  // Behavioural intent signals from the page itself.
  const p = path.toLowerCase();
  if (interestForPath(p)) {
    void addIntent(a, "trailer_page");
    if (repeatView) void addIntent(a, "trailer_page_repeat");
  }
  if (/inventory|trailers-perris|for-sale/.test(p)) void addIntent(a, "inventory_page");
  if (/financing/.test(p)) void addIntent(a, "financing_page");
  if (/quote|request-a-quote/.test(p)) void addIntent(a, "quote_page");
  if (p.startsWith("/build-your") && !repeatView)
    void trackSiteEvent("build_started", { name: "Builder Opened", label: path });

  try {
    await sessionReady;
    // The session rollup (page count, intent score, interest) is derived
    // server-side from this append-only event — no privileged RPC required.
    await supabase.from("site_events").insert({
      session_key: a.sessionKey,
      event_type: "page_view",
      path,
      metadata: {
        visitor_key: a.visitorKey,
        page_view_count: a.viewedPaths.length,
        primary_interest: a.primaryInterest ?? null,
      } as never,
    });
  } catch {
    /* ignore */
  }
};

const EVENT_INTENT: Record<string, keyof typeof INTENT_RULES> = {
  phone_click: "phone_click",
  email_click: "email_click",
  quote_start: "quote_started",
  quote_submit: "quote_submitted",
};

/**
 * Inventory context for the page currently being viewed.
 * When set, every tracked event on that page (call, quote, financing)
 * carries the stock number so the admin map can attribute sales intent
 * to a specific clearance unit.
 */
let inventoryContext: { stock?: string; name?: string } = {};

export const setInventoryContext = (ctx: { stock?: string; name?: string } | null): void => {
  inventoryContext = ctx ?? {};
};

/** Records a tracked interaction (quote button, phone, email, financing…). */
export const trackSiteEvent = async (
  eventType: string,
  options: {
    name?: string;
    label?: string;
    destination?: string;
    metadata?: Record<string, unknown>;
  } = {},
): Promise<void> => {
  const a = getAttribution();
  if (!a) return;

  const rule = EVENT_INTENT[eventType];
  if (rule) void addIntent(a, rule);

  try {
    await sessionReady;
    await supabase.from("site_events").insert({
      session_key: a.sessionKey,
      event_type: eventType,
      event_name: options.name ?? null,
      label: options.label ?? null,
      destination: options.destination ?? null,
      path: window.location.pathname,
      metadata: ({
        ...(options.metadata ?? {}),
        ...(inventoryContext.stock ? { stock_number: inventoryContext.stock } : {}),
        ...(inventoryContext.name ? { unit_name: inventoryContext.name } : {}),
        visitor_key: a.visitorKey,
        ab_test: ACTIVE_TEST_ID,
        ab_variant: getVariant(),
        device: a.deviceType ?? null,
        landing_page: a.firstLandingPage ?? null,
      }) as never,
    });
  } catch {
    /* ignore */
  }
};

/** Records a view of one clearance/inventory listing (stock-number level). */
export const trackStockView = (stock: string, unitName?: string): void => {
  setInventoryContext({ stock, name: unitName });
  void trackSiteEvent("stock_view", {
    name: "Inventory Listing View",
    label: unitName ?? stock,
    metadata: { stock_number: stock },
  });
};

/* ---------------- Engagement + scroll depth ---------------- */

let engagementStarted = false;

/** Starts heartbeat + scroll-depth measurement. Safe to call once per app load. */
export const startEngagementTracking = (): void => {
  if (!isBrowser() || engagementStarted) return;
  engagementStarted = true;

  let unsyncedSeconds = 0;
  let currentPath = window.location.pathname;
  let milestones = new Set<number>();

  const syncSession = async (seconds: number, scroll: number) => {
    const a = getAttribution();
    if (!a) return;
    a.engagementSeconds += seconds;
    a.maxScrollDepth = Math.max(a.maxScrollDepth, scroll);
    a.lastSeenAt = Date.now();
    persist(a);
    try {
      await sessionReady;
      await supabase.from("site_events").insert({
        session_key: a.sessionKey,
        event_type: "session_activity",
        path: a.lastPage,
        metadata: {
          visitor_key: a.visitorKey,
          engagement_seconds: a.engagementSeconds,
          max_scroll_depth: a.maxScrollDepth,
        } as never,
      });
    } catch {
      /* ignore */
    }
  };

  const scrollPercent = (): number => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 100;
    return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
  };

  const onScroll = () => {
    if (window.location.pathname !== currentPath) {
      currentPath = window.location.pathname;
      milestones = new Set();
    }
    const pct = scrollPercent();
    [25, 50, 75, 100].forEach((m) => {
      if (pct >= m && !milestones.has(m)) {
        milestones.add(m);
        void trackSiteEvent("scroll_depth", {
          name: `${m}%`,
          label: currentPath,
          metadata: { depth: m },
        });
      }
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });

  // Delegated CTA tracking — one listener covers every button and link.
  document.addEventListener(
    "click",
    (event) => {
      const target = (event.target as HTMLElement | null)?.closest(
        "a, button",
      ) as HTMLAnchorElement | HTMLButtonElement | null;
      if (!target) return;

      const href = (target as HTMLAnchorElement).href ?? "";
      const text = (target.textContent ?? "").trim().slice(0, 60);
      const label = target.getAttribute("aria-label") || text;

      if (/^tel:/i.test(href)) {
        void trackSiteEvent("phone_click", { name: "Call Juan", label, destination: href });
        return;
      }
      if (/^sms:/i.test(href)) {
        void trackSiteEvent("sms_click", { name: "Text Juan", label, destination: href });
        void trackSiteEvent("text_click", { name: "Text Juan", label, destination: href });
        return;
      }
      if (/delivery/i.test(href) || /delivery quote/i.test(text)) {
        void trackSiteEvent("delivery_quote_clicked", { name: "Delivery Quote", label, destination: href });
      }
      if (/^mailto:/i.test(href)) {
        void trackSiteEvent("email_click", { name: "Email Juan", label, destination: href });
        return;
      }
      if (/financing/i.test(href) || /financ/i.test(text)) {
        void trackSiteEvent("financing_click", { name: "Financing", label, destination: href });
        return;
      }
      if (/quote|pricing/i.test(href) || /quote|pricing/i.test(text)) {
        void trackSiteEvent("quote_start", { name: "Quote CTA", label, destination: href });
        return;
      }
      if (target.tagName === "BUTTON" || /^https?:/.test(href)) {
        void trackSiteEvent("cta_click", { name: label || "Button", label, destination: href });
      }
    },
    true,
  );

  const heartbeat = window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    unsyncedSeconds += 15;
    if (unsyncedSeconds >= 30) {
      void syncSession(unsyncedSeconds, scrollPercent());
      unsyncedSeconds = 0;
    }
  }, 15000);

  const flush = () => {
    if (unsyncedSeconds > 0) {
      void syncSession(unsyncedSeconds, scrollPercent());
      unsyncedSeconds = 0;
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", () => window.clearInterval(heartbeat));
};

/* ---------------- Leads ---------------- */

export interface LeadInput {
  full_name?: string;
  phone?: string;
  email?: string;
  trailer_type?: string;
  trailer_size?: string;
  gvwr?: string;
  location?: string;
  pickup_location?: string;
  delivery_preference?: string;
  financing_interest?: string;
  message?: string;
  /** Guided discovery answers, stored as JSON for the admin lead view. */
  discovery?: Record<string, unknown>;
  industry?: string;
  hauling_material?: string;
  hitch?: string;
  cdl_preference?: string;
  purchase_timeline?: string;
  tow_vehicle?: string;
  zip_code?: string;
  city?: string;
  region?: string;
  origin_page?: string;
  preferred_contact?: string;
  best_contact_time?: string;
  company?: string;
  /** Which form produced this lead, e.g. "quick_quote", "trailer_builder". */
  form_type?: string;
  /** Invisible honeypot value — real customers always leave this empty. */
  company_website?: string;
  /** True when the visitor answered job, trailer, tow vehicle and capacity. */
  qualified?: boolean;
}

export interface LeadResult {
  ok: boolean;
  leadId: string | null;
  error?: string;
}

/**
 * Saves a quote/contact submission to our own lead database FIRST, then lets
 * the backend send Juan's notification email. The lead is stored even when
 * email delivery fails, so a quote request can never disappear.
 */
/**
 * Sales lead capture is retired (education repositioning). Kept as a no-op so
 * any leftover caller fails safely; historical leads stay in the admin.
 */
export const submitLead = async (_lead: LeadInput): Promise<LeadResult> => ({
  ok: false,
  leadId: null,
  error: "This form is no longer available. Please use the contact page.",
});

/** Legacy Formspree mirror — disabled. */
export const mirrorToFormspree = (_fields: Record<string, unknown>) => {
  /* disabled */
};

export type ContactTopic = "question" | "feedback" | "media" | "other";

/** General educational contact form. Stored separately from sales leads. */
export const submitContact = async (input: {
  full_name: string;
  email: string;
  topic: ContactTopic;
  message: string;
  honeypot?: string;
}): Promise<{ ok: boolean; error?: string }> => {
  const a = getAttribution();
  try {
    await sessionReady;
    const { data, error } = await supabase.functions.invoke("contact-intake", {
      body: {
        full_name: input.full_name,
        email: input.email,
        topic: input.topic,
        message: input.message,
        company_website: input.honeypot || undefined,
        origin_page: isBrowser() ? window.location.pathname : undefined,
        session_key: a?.sessionKey ?? undefined,
        visitor_key: a?.visitorKey ?? undefined,
      },
    });
    if (error || !data?.success) {
      return { ok: false, error: (data as { error?: string } | null)?.error ?? "We couldn't send your message. Please try again." };
    }
    void trackSiteEvent("contact_submit", { name: "Contact Message Sent", label: input.topic });
    return { ok: true };
  } catch {
    return { ok: false, error: "We couldn't send your message. Please try again." };
  }
};
